<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Vídeos</h1>
      <v-btn color="primary" @click="handleCreateVideo">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Vídeo
      </v-btn>
    </div>

    <!-- Tabela -->
    <v-card class="elevation-4">
      <v-card-text>
        <!-- Filtro -->
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchTerm"
              placeholder="Pesquisar vídeo"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="columns"
          :items="paginatedVideos"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :loading="isLoading"
          :no-data-text="noDataMessage"
          class="elevation-0"
        >
          <template #item.title="{ item }">
            <span>{{ item.title }}</span>
          </template>

          <template #item.description="{ item }">
            <span>{{ item.description }}</span>
          </template>

          <template #item.type="{ item }">
            <v-chip
              :color="item.type === 'internal' ? 'info' : 'secondary'"
              label
            >
              {{ formatType(item.type) }}
            </v-chip>
          </template>

          <template #item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template #item.actions="{ item }">
            <v-btn
              icon
              variant="text"
              color="warning" size="large"
              @click="handleEdit(item)"
              :title="`Editar ${item.title}`"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Paginação -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="page"
            :length="pageCount"
            rounded="circle"
            color="primary"
          />
        </div>

        <!-- Erro -->
        <v-alert
          v-if="hasError && !isLoading"
          type="error"
          variant="tonal"
          class="mt-4 text-center"
        >
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingOverlay } from "@/store/loadingOverlay";

const router = useRouter();
const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();

const videos = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const searchTerm = ref("");

// paginação
const page = ref(1);
const itemsPerPage = 10;

const isActiveTagMap = {
  0: "error",
  1: "success",
};

const filteredVideos = computed(() => {
  let result = videos.value;

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter((video) =>
      video.title.toLowerCase().includes(term)
    );
  }

  return result;
});

const noDataMessage = computed(() => {
  if (isLoading.value) return "Carregando...";
  if (filteredVideos.value.length === 0) return "Nenhum vídeo encontrado.";
  return "";
});

const pageCount = computed(() =>
  Math.ceil(filteredVideos.value.length / itemsPerPage)
);

const paginatedVideos = computed(() => {
  const start = (page.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredVideos.value.slice(start, end);
});

const columns = [
  { title: "Título", key: "title", sortable: true },
  { title: "Descrição", key: "description", sortable: true },
  { title: "Tipo", key: "type", sortable: true },
  { title: "Situação", key: "is_active", sortable: true },
  { title: "Ações", key: "actions", sortable: false },
];

const loadVideos = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadingOverlay.startLoading();
  try {
    const response = await api.get("/videos");
    videos.value = response.data.data;
  } catch (error) {
    console.error("Erro ao carregar vídeos:", error);
    hasError.value = true;
    toastStore.showToast({
      message:
        error?.response?.data?.error ||
        "Ocorreu um erro ao carregar a lista de vídeos.",
      color: "error",
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

const handleCreateVideo = () => {
  router.push("/app/videos/novo");
};

const handleEdit = (row) => {
  router.push({ name: "video-edit", params: { id: row.id } });
};

function formatIsActive(is_active) {
  switch (is_active) {
    case 0:
      return "Desativado";
    case 1:
      return "Ativo";
    default:
      return is_active;
  }
}

function formatType(type) {
  switch (type) {
    case "internal":
      return "Interno";
    case "external":
      return "Externo";
    default:
      return type;
  }
}

onMounted(() => {
  loadVideos();
});
</script>
